
import React, { useState } from 'react';
import { UserProfile } from '../types';
import { LayoutGrid, Kanban, Settings, LogOut, Shield, Activity, Crown, Zap, Sun, HelpCircle, Lock } from 'lucide-react';
import { Button } from './Button';
import { LEVELS, APP_LOGO } from '../constants';
import { HelpModal } from './HelpModal';

interface LayoutProps {
  user: UserProfile;
  currentView: 'matrix' | 'kanban' | 'settings' | 'analytics' | 'focus' | 'daily';
  onChangeView: (view: 'matrix' | 'kanban' | 'settings' | 'analytics' | 'daily') => void;
  onLogout: () => void;
  // Fix: use React.ReactNode instead of the invalid React.Node
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ 
  user, 
  currentView, 
  onChangeView, 
  onLogout,
  children 
}) => {
  const [showHelp, setShowHelp] = useState(false);

  const currentLevel = LEVELS.find(l => l.level === user.level) || LEVELS[0];
  const nextLevel = LEVELS.find(l => l.level === user.level + 1);
  const xpEnd = nextLevel ? nextLevel.xp : user.xp * 1.5; 
  const progressPercent = Math.min(100, Math.max(0, ((user.xp - currentLevel.xp) / (xpEnd - currentLevel.xp)) * 100));

  const navItems = [
    { id: 'matrix', label: 'Matrix', icon: LayoutGrid, minLevel: 1 },
    { id: 'daily', label: 'Daily Plan', icon: Sun, minLevel: 1 },
    { id: 'kanban', label: 'Kanban', icon: Kanban, minLevel: 1 },
    { id: 'analytics', label: 'Pulse', icon: Activity, minLevel: 4 },
    { id: 'settings', label: 'Settings', icon: Settings, minLevel: 1 },
  ];

  const XPBar = () => (
    <div className="px-1 py-1 w-full">
       <div className="flex justify-between items-end mb-1">
          <span className="text-xs font-bold text-gray-700 dark:text-gray-200 flex items-center gap-1">
             <Crown size={12} className="text-amber-500" />
             {currentLevel.title}
          </span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">Lvl {user.level}</span>
       </div>
       <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
             className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-1000 ease-out" 
             style={{width: `${progressPercent}%`}}
          />
       </div>
    </div>
  );

  return (
    <div className="flex h-screen w-full bg-gray-50 dark:bg-dark-bg midnight:bg-midnight-bg flex-col md:flex-row transition-colors duration-500 text-gray-900 dark:text-dark-text">
      <div className="bg-white dark:bg-dark-surface midnight:bg-midnight-surface border-b md:border-b-0 md:border-r border-gray-200 dark:border-dark-border midnight:border-midnight-border md:w-64 flex-shrink-0 flex flex-col justify-between p-4 z-20">
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg border-2 border-blue-400">
                <img src={APP_LOGO} className="w-full h-full object-cover" alt="Logo" />
              </div>
              <span className="font-bold text-gray-800 dark:text-white text-lg hidden md:block">Eisenhower Pro</span>
            </div>
          </div>
          
          <XPBar />

          <nav className="flex md:flex-col gap-1 w-full overflow-x-auto md:overflow-visible">
            {navItems.map((item) => {
              const isLocked = user.level < item.minLevel;
              const Icon = item.icon;
              return (
                <button 
                  key={item.id}
                  disabled={isLocked}
                  onClick={() => onChangeView(item.id as any)} 
                  className={`flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap md:w-full transition-all ${
                    currentView === item.id 
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                      : isLocked 
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </div>
                  {isLocked && <Lock size={12} className="opacity-50" />}
                </button>
              );
            })}
          </nav>
        </div>
        
        <div className="hidden md:flex flex-col gap-4 border-t border-gray-100 dark:border-dark-border pt-4">
          <div className="flex items-center gap-3 px-2">
            {user.photoURL ? <img src={user.photoURL} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs">{user.email?.charAt(0).toUpperCase()}</div>}
            <div className="flex flex-col overflow-hidden">
              <span className="text-sm font-medium truncate">{user.displayName || 'User'}</span>
              <span className="text-[10px] text-gray-500 uppercase font-bold">{user.isPro ? 'Pro Member' : 'Free'}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} className="w-full justify-start gap-2"><LogOut size={14} />Logout</Button>
        </div>
      </div>
      <main className="flex-1 overflow-auto p-4 md:p-8 relative">{children}</main>
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} view={currentView} />
    </div>
  );
};
